import * as R from 'ramda'

import contentfulClient from 'clients/contentful'
import apolloClient from 'clients/apollo'

import {
  getCountryStateSlugs,
  getStateBySlug,
  getS3Content,
} from './queries.graphql'
import LocalPage, { LOCAL_PAGE_TYPE } from '../personal-trainers'

export const SURVEY_ID = 3

export async function getStaticProps({
  params: { cityOrCountrySlug: countrySlug, stateSlug },
}) {
  const localPageType = LOCAL_PAGE_TYPE.STATE
  const env = process.env.APP_ENV
  let config, locationPayload, surveyPayload, overrideDefault

  // Loads local page content from our S3 cache in staging/production
  // to avoid hitting Contentful API rate limits while building
  if (env !== 'development') {
    const localPageContentPromise = contentfulClient.getEntries({
      content_type: 'localPageState',
      'fields.localPageState[in]': `DEFAULT,${stateSlug}`,
      order: 'sys.createdAt',
      include: 10,
    })
    const surveyContentPromise = contentfulClient.getEntries({
      content_type: 'survey',
      'fields.id[in]': SURVEY_ID,
      include: 10,
    })

    const [location, contentPayload, survey] = await Promise.all([
      apolloClient.query({
        query: getStateBySlug,
        variables: { stateSlug, countrySlug },
      }),
      localPageContentPromise,
      surveyContentPromise,
    ])

    // Allow overriding default content if found a Contentful state specific page
    overrideDefault = contentPayload?.items[1]
    config = overrideDefault || R.head(contentPayload.items)

    surveyPayload = survey
    locationPayload = location
  } else {
    const localPageContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: `localPageState/${stateSlug}.json` },
    })
    const defaultLocalPageContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: 'localPageState/DEFAULT.json' },
    })
    const surveyContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: 'survey/3.json' },
    })

    const [location, defaultContentPayload, contentPayload, survey] =
      await Promise.all([
        apolloClient.query({
          query: getStateBySlug,
          variables: { stateSlug, countrySlug },
        }),
        defaultLocalPageContentPromise,
        localPageContentPromise,
        surveyContentPromise,
      ])

    // Allow overriding default content if found a Contentful state specific page
    overrideDefault = R.head(
      R.pathOr([], ['data', 's3Content', 'items'], contentPayload)
    )
    config =
      overrideDefault || R.head(defaultContentPayload.data.s3Content.items)

    surveyPayload = survey.data.s3Content
    locationPayload = location
  }

  const locationDetails = overrideDefault
    ? {
        state: {
          name: overrideDefault.fields.stateName,
          abbreviation: overrideDefault.fields.stateAbbreviation,
          slug: overrideDefault.fields.stateSlug,
          country: {
            slug: overrideDefault.fields.countrySlug,
            abbreviation: overrideDefault.fields.countryAbbreviation,
          },
        },
      }
    : locationPayload.data.localPageStateLocation

  if (!locationDetails) {
    return {
      redirect: {
        destination: `/local/${countrySlug}/personal-trainers`,
        permanent: true,
      },
    }
  }

  if (!locationDetails?.stateCoaches) {
    let emptyStateContent

    // Load empty state page
    if (env === 'development') {
      emptyStateContent = await contentfulClient.getEntries({
        content_type: 'localPageState',
        'fields.localPageState[in]': `EMPTY_STATE`,
        order: 'sys.createdAt',
        include: 10,
      })
    } else {
      emptyStateContent = await apolloClient.query({
        query: getS3Content,
        variables: { key: 'localPageState/EMPTY_STATE.json' },
      })
    }

    return {
      props: {
        localPageType,
        statusCode: 200,
        fields: R.head(emptyStateContent.data.s3Content.items).fields,
        survey: surveyPayload.items[0],
        locationDetails,
      },
      revalidate: 600,
    }
  }

  return {
    props: {
      statusCode: 200,
      fields: config.fields,
      survey: surveyPayload.items[0],
      locationDetails,
      localPageType,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths() {
  const { data } = await apolloClient.query({
    query: getCountryStateSlugs,
  })

  const paths = data.countryStateSlugs.map(
    slug => `/local/${slug}/personal-trainers`
  )

  return {
    paths,
    fallback: 'blocking',
  }
}

export default LocalPage
